import { ChevronDown } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@predoc/cow';
import { TruncatedText } from '@/components';
import { cn } from '@/lib/utils';
import { formatDateOnly } from '@/utils/date';
import type { FacesheetReference } from '../types';

/** Keeps View (0) / View (1) / View (N)▾ the same footprint in the criteria table. */
const VIEW_BUTTON_WIDTH_CLASS = 'w-[6.5rem]';

interface FacesheetCriteriaReferencesButtonProps {
  references: FacesheetReference[];
  onSelectReference: (referenceId?: string) => void;
  className?: string;
}

export const FacesheetCriteriaReferencesButton = ({
  references,
  onSelectReference,
  className,
}: FacesheetCriteriaReferencesButtonProps) => {
  const count = references.length;

  if (count === 0) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled
        className={cn(VIEW_BUTTON_WIDTH_CLASS, 'whitespace-nowrap', className)}
        aria-label="No references available"
      >
        View (0)
      </Button>
    );
  }

  if (count === 1) {
    const [reference] = references;

    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onSelectReference(reference.id)}
        className={cn(VIEW_BUTTON_WIDTH_CLASS, 'whitespace-nowrap', className)}
        aria-label="View 1 reference"
      >
        View (1)
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(
            VIEW_BUTTON_WIDTH_CLASS,
            'gap-0 overflow-hidden p-0 whitespace-nowrap',
            className,
          )}
          aria-label={`View ${count} references`}
        >
          <span className="flex flex-1 items-center justify-center px-2">
            View ({count})
          </span>
          <span
            className="border-input flex w-8 shrink-0 items-center justify-center self-stretch border-l"
            aria-hidden="true"
          >
            <ChevronDown className="size-3" />
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[24rem] p-0">
        <div className="border-border grid grid-cols-[minmax(0,1fr)_auto] gap-x-6 border-b px-4 py-2">
          <span className="text-muted-foreground text-sm font-medium">
            Reference
          </span>
          <span className="text-muted-foreground text-right text-sm font-medium">
            Date Referenced
          </span>
        </div>
        {references.map((reference) => (
          <DropdownMenuItem
            key={reference.id}
            onSelect={() => onSelectReference(reference.id)}
            className="border-border grid grid-cols-[minmax(0,1fr)_auto] gap-x-6 rounded-none border-b px-4 py-2.5 text-sm last:border-b-0"
          >
            <div className="min-w-0">
              <TruncatedText
                text={reference.label}
                className="text-foreground text-left"
                tooltipSide="left"
              />
            </div>
            <span className="text-foreground shrink-0 text-right">
              {formatDateOnly(reference.dateReferenced) ?? ''}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
